import { useState } from 'react'
import type { UpdateStorageNodePayload } from '@/api/storageApi'
import { useUpdateStorageNode } from '@/hooks/useStorage'
import { useUndoToast } from '@/components/inventory/storage/UndoToast'
import type { StorageListRow } from '@/lib/storageList'
import { InlineSelect } from './InlineSelect'

const NONE = '__none__'

/**
 * The list's "Zone" cell. Same edit-in-place pattern as `KeptAtCell`, over the
 * picking-zone catalog plus a "None" choice — a zone is a walk, not a
 * temperature, so it has its own control rather than riding on Kept at.
 *
 * Purely controlled off `row`, for the same reason: a refused change
 * (Review Focus 2) must leave `row.zoneId` exactly where it was. The edit and
 * its Undo both commit through `commit`, so a refused Undo shows the same
 * inline error a refused edit would. The trigger is disabled (readOnly) while
 * `update` is pending, so a second pick cannot race the first.
 */
export function ZoneCell({
  row,
  zones,
  readOnly,
}: {
  row: StorageListRow
  zones: { id: string; name: string }[]
  readOnly: boolean
}) {
  const update = useUpdateStorageNode()
  const showUndoToast = useUndoToast()
  const [error, setError] = useState<string | null>(null)

  const value = row.zoneId ?? NONE
  const options = [
    { value: NONE, label: <span className='text-muted-foreground'>None</span> },
    ...zones.map((z) => ({ value: z.id, label: z.name })),
  ]

  async function commit(payload: UpdateStorageNodePayload, onCommitted?: () => void) {
    setError(null)
    try {
      await update.mutateAsync({ id: row.nodeId, ...payload })
      onCommitted?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not change the zone.')
    }
  }

  function handleChange(next: string) {
    const nextZoneId = next === NONE ? null : next
    const nextLabel = next === NONE ? 'None' : (zones.find((z) => z.id === next)?.name ?? next)
    const previousZoneId = row.zoneId

    void commit({ zoneId: nextZoneId }, () => {
      showUndoToast(
        <>
          Zone for <b>{row.code}</b> changed to <b>{nextLabel}</b>
        </>,
        () => void commit({ zoneId: previousZoneId }),
      )
    })
  }

  return (
    <div>
      <InlineSelect
        value={value}
        options={options}
        onChange={handleChange}
        readOnly={readOnly || update.isPending}
        renderValue={() =>
          row.zoneName ? <span>{row.zoneName}</span> : <span className='text-muted-foreground'>Add zone</span>
        }
        ariaLabel={`Zone for ${row.code}`}
        header={`Zone · ${row.code}`}
      />
      {error && <p className='mt-1 text-xs text-destructive'>{error}</p>}
    </div>
  )
}
